import { defineNuxtPlugin } from 'nuxt/app';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getAuth, type Auth } from 'firebase/auth';
import type { Messaging } from 'firebase/messaging';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

function createFirebaseFallback() {
    return {
        firebaseApp: null as FirebaseApp | null,
        db: null as Firestore | null,
        auth: {
            currentUser: null,
            app: null,
        } as unknown as Auth,
        storage: null as FirebaseStorage | null,
        messaging: null as Messaging | null,
    };
}

function hasFirebaseClientConfig(firebaseConfig: Record<string, unknown>) {
    return Object.values(firebaseConfig).every((value) => String(value ?? '').trim().length > 0);
}

export default defineNuxtPlugin(() => {
    const config = useRuntimeConfig();

    const firebaseConfig = {
        apiKey: config.public.firebaseApiKey,
        authDomain: config.public.firebaseAuthDomain,
        projectId: config.public.firebaseProjectId,
        storageBucket: config.public.firebaseStorageBucket,
        messagingSenderId: config.public.firebaseMessagingSenderId,
        appId: config.public.firebaseAppId,
    };

    if (!hasFirebaseClientConfig(firebaseConfig)) {
        console.warn('[firebase] Missing client configuration. Firebase services are disabled for this session.');
        return {
            provide: createFirebaseFallback(),
        };
    }

    try {
        const firebaseApp = initializeApp(firebaseConfig);
        const appCheckSiteKey = String(config.public.firebaseAppCheckSiteKey || '').trim();
        if (appCheckSiteKey) {
            try {
                initializeAppCheck(firebaseApp, {
                    provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
                    isTokenAutoRefreshEnabled: true,
                });
            } catch (appCheckError) {
                console.error('[firebase] Failed to initialize App Check.', appCheckError);
            }
        }
        const db = getFirestore(firebaseApp);
        const auth = getAuth(firebaseApp);
        const storage = getStorage(firebaseApp);

        return {
            provide: {
                firebaseApp,
                db,
                auth,
                storage,
                messaging: null,
            },
        };
    } catch (error) {
        console.error('[firebase] Failed to initialize client services.', error);

        return {
            provide: createFirebaseFallback(),
        };
    }
});
